import type { ClassDef, ClassId } from './types';

/**
 * The three playable classes. Everything is data-driven so balance tweaks
 * stay in one place and are automatically shared with the client.
 */
export const CLASSES: Record<ClassId, ClassDef> = {
  warrior: {
    id: 'warrior',
    name: 'Warrior',
    tagline: 'Melee bruiser',
    description:
      'Heavy armour and a sweeping blade. Hits everything in front of you and shrugs off damage.',
    color: '#c2603c',
    accent: '#f0a868',
    speed: 208,
    /**
     * Mana is an ability resource only - basic attacks are free. Regeneration
     * is set just above the cost of using both abilities the moment they come
     * off cooldown, so a player who plays optimally is never locked out, while
     * spamming beyond that still costs something.
     */
    mpRegen: 4.5,
    attack: { kind: 'melee', range: 68, cooldown: 0.78, arc: Math.PI / 2.6 },
    abilities: ['whirlwind', 'charge'],
    base: { maxHp: 170, maxMp: 40, attack: 15, defense: 9 },
    growth: { maxHp: 19, maxMp: 3, attack: 2.4, defense: 1.5 },
  },
  ranger: {
    id: 'ranger',
    name: 'Ranger',
    tagline: 'Ranged skirmisher',
    description:
      'Quick on your feet with a bow that reaches across the screen. Kite monsters all day.',
    color: '#4f9d55',
    accent: '#a8e06a',
    speed: 232,
    mpRegen: 6.5,
    attack: {
      kind: 'projectile',
      range: 470,
      cooldown: 0.62,
      projectile: { speed: 760, radius: 5, life: 0.9, color: '#d8f07a' },
    },
    abilities: ['volley', 'pierce'],
    base: { maxHp: 118, maxMp: 60, attack: 13, defense: 5 },
    growth: { maxHp: 12, maxMp: 5, attack: 2.1, defense: 0.9 },
  },
  mage: {
    id: 'mage',
    name: 'Mage',
    tagline: 'Glass cannon',
    description:
      'Devastating arcane bolts from a distance. Fragile, so never let anything touch you.',
    color: '#6c5ce7',
    accent: '#b39dff',
    speed: 220,
    mpRegen: 9,
    attack: {
      kind: 'projectile',
      range: 420,
      cooldown: 0.95,
      projectile: { speed: 520, radius: 9, life: 1.1, color: '#c9a7ff' },
    },
    abilities: ['nova', 'blink'],
    base: { maxHp: 88, maxMp: 110, attack: 20, defense: 3 },
    growth: { maxHp: 9, maxMp: 11, attack: 3.2, defense: 0.5 },
  },
};

export const CLASS_ORDER: ClassId[] = ['warrior', 'ranger', 'mage'];

export function getClass(id: ClassId): ClassDef {
  return CLASSES[id] ?? CLASSES.warrior;
}

export function isClassId(value: unknown): value is ClassId {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(CLASSES, value);
}
